import React, { JSX } from 'react';
import style from './style.module.scss';
import { icons } from '../../lib/helpers/icons';
import { AppIconType } from '../../lib/typegen/app-icon-type';
import logger from '../../lib/helpers/logger';

export interface GenericButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	icon?: AppIconType;
	isAltColor?: boolean;
	innerText?: string;
	hasGlow?: boolean;
	design?: 'centered' | 'flush-left';
	innerHtml?: JSX.Element;
	isHorizontallyCentered?: boolean;
	isFullWidth?: boolean;
	isGrowHorizontally?: boolean;
	centeredContent?: boolean;
}

export default function GenericButton({
	type = 'button',
	icon,
	isAltColor,
	className,
	innerText,
	innerHtml,
	hasGlow,
	design = 'centered',
	disabled,
	isHorizontallyCentered = true,
	isFullWidth = false,
	isGrowHorizontally = true,
	centeredContent = false,
	style: inlineStyle,
	onClick,
	...props
}: GenericButtonProps) {
	let Icon: AppIcon | null = null;
	const modifier: string[] = [];

	if (isAltColor) modifier.push(style['button--alt-color']);
	if (hasGlow) modifier.push(style['button--glow']);
	if (design === 'flush-left' || !!icon) modifier.push(style['button--flush-left']);
	if (isFullWidth) modifier.push(style['button--full-width']);
	if (isHorizontallyCentered) modifier.push(style['button--horizontally-centered']);
	if (isGrowHorizontally) modifier.push(style['button--grow-horizontally']);

	if (disabled) modifier.push(style['button--disabled']);

	if (icon) {
		Icon = icons[icon];

		switch (icon) {
			case AppIconType.CancelSolid:
				modifier.push(style['button--cancel-solid']);
				break;
			case AppIconType.AcceptSolid:
				modifier.push(style['button--accept-solid']);
				break;
		}
	}

	const extractText = (element: React.ReactNode): string => {
		if (typeof element === 'string') return element;
		if (typeof element === 'number') return String(element);
		if (React.isValidElement(element)) {
			const children = (element.props as { children?: React.ReactNode })?.children;
			if (Array.isArray(children)) return children.map(extractText).join('');
			return extractText(children);
		}
		return '';
	};

	const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		const buttonLabel = (innerText ?? extractText(innerHtml)) || 'unknown';
		logger.logGeneral.log({}, `GenericButton clicked [${buttonLabel}]`, {
			innerText,
			icon,
			disabled,
			type,
		});
		onClick?.(e);
	};

	return (
		<button
			onClick={handleClick}
			{...props}
			disabled={disabled}
			type={type}
			className={[className, style.button, ...modifier].join(' ')}
			style={inlineStyle}
		>
			{Icon && <Icon aria-hidden='true' className={style.button__icon} />}
			{innerText ? (
				<span className={[style.button__text, centeredContent ? style['button__text--centered'] : ''].join(' ')}>{innerText}</span>
			) : (
				innerHtml
			)}
		</button>
	);
}
